/**
 * The month board (PLAN §7): who is done, held or missing in a round, from ONE call to
 * get_inspection_board. Every chip, count, late flag and condition on it is the server's; the board
 * filters and searches the rows it was given and works out nothing else.
 *
 * A desktop table and phone cards render the same rows. On a phone the cards are the inspector's
 * picking list, so Done rows drop out of it unless the Done tile or a search asks for them.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Camera, ChevronLeft, ChevronRight, RotateCcw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { parseBusinessDay } from '@/lib/format';
import { format } from 'date-fns';
import {
    capturePath,
    contractInspectionPath,
    formatKm,
    formatKmChange,
    formatShortDay,
    roundName,
    rowActions,
    shiftPeriod,
    windowLabel,
    type BoardChip,
    type BoardRow,
    type HeldWithoutContract,
} from '@/lib/inspections';
import { browserStorage, isResumable, listDrafts } from '@/lib/inspectionDraft';
import { useInspectionBoard } from './inspectionApi';
import { BoardChipBadge, OpenCounts, ResultBadge } from './InspectionChip';
import { DriverContact } from './DriverContact';

type TileKey = Extract<BoardChip, 'not_yet' | 'done' | 'held' | 'missed'>;

const TILES: Array<{ key: TileKey; label: string; hint: string; active: string; idle: string }> = [
    { key: 'not_yet', label: 'Not yet', hint: 'owed, window open', active: 'border-sky-500 bg-sky-50 dark:bg-sky-950/40', idle: 'text-sky-700 dark:text-sky-400' },
    { key: 'done', label: 'Done', hint: 'presented or kept, not held now', active: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40', idle: 'text-emerald-700 dark:text-emerald-400' },
    { key: 'held', label: 'Held', hint: 'kept at the yard', active: 'border-red-500 bg-red-50 dark:bg-red-950/40', idle: 'text-red-700 dark:text-red-400' },
    { key: 'missed', label: 'Missed', hint: 'window closed, no visit', active: 'border-orange-500 bg-orange-50 dark:bg-orange-950/40', idle: 'text-orange-700 dark:text-orange-400' },
];

const normalisePlate = (s: string | null | undefined) => (s ?? '').toLowerCase().replace(/[\s-]/g, '');

interface DraftMarks {
    monthly: Set<number>;
    recheck: Set<number>;
}

function RowActions({ row, drafts, large }: { row: BoardRow; drafts: DraftMarks; large?: boolean }) {
    const navigate = useNavigate();
    const { monthly, recheck } = rowActions(row);
    if (!monthly && !recheck) return null;
    const go = (e: React.MouseEvent, path: string) => {
        e.stopPropagation();
        navigate(path);
    };
    const size = large ? 'default' : 'sm';
    return (
        <div className={cn('flex gap-2', large && 'w-full [&>*]:flex-1')}>
            {monthly && (
                <Button size={size} onClick={(e) => go(e, capturePath(row.vehicle_id, 'monthly', row.contract_id))}>
                    <Camera aria-hidden />
                    {drafts.monthly.has(row.vehicle_id) ? 'Resume' : 'Inspect'}
                </Button>
            )}
            {recheck && (
                <Button size={size} variant={monthly ? 'outline' : 'default'} onClick={(e) => go(e, capturePath(row.vehicle_id, 'recheck', row.contract_id))}>
                    <RotateCcw aria-hidden />
                    {drafts.recheck.has(row.vehicle_id) ? 'Resume re-check' : 'Re-check'}
                </Button>
            )}
        </div>
    );
}

function Odometer({ row }: { row: BoardRow }) {
    if (row.odometer_km === null) return <span className="text-muted-foreground">—</span>;
    const change = row.previous_odometer_km !== null ? row.odometer_km - row.previous_odometer_km : null;
    return (
        <span className="tabular-nums">
            {formatKm(row.odometer_km)}
            {change !== null && (
                <span className={cn('ml-1.5 text-xs', change < 0 ? 'text-orange-700 dark:text-orange-400' : 'text-muted-foreground')}>
                    {formatKmChange(change)}
                </span>
            )}
        </span>
    );
}

function Inspected({ row }: { row: BoardRow }) {
    if (!row.inspected_on) return <span className="text-muted-foreground">—</span>;
    return (
        <span className="inline-flex flex-wrap items-center gap-1.5">
            <span className="tabular-nums">{formatShortDay(row.inspected_on)}</span>
            <ResultBadge result={row.result} />
        </span>
    );
}

/**
 * `actionable` is false on any round but the one whose span holds today: record_inspection files a
 * monthly under today's round, so an Inspect on a past round's Missed row would not make that miss
 * up (PLAN §3.5), and before the first round a look ahead has nothing to record yet.
 */
function BoardTable({ rows, drafts, muted, actionable }: { rows: BoardRow[]; drafts: DraftMarks; muted?: boolean; actionable: boolean }) {
    const navigate = useNavigate();
    return (
        <Table className={cn(muted && 'opacity-75')}>
            <TableHeader>
                <TableRow>
                    <TableHead>Driver</TableHead>
                    <TableHead>Car</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Inspected</TableHead>
                    <TableHead>Open items</TableHead>
                    <TableHead>Odometer</TableHead>
                    <TableHead className="w-[1%]"><span className="sr-only">Actions</span></TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.contract_id} className="cursor-pointer" onClick={() => navigate(contractInspectionPath(row.contract_id))}>
                        <TableCell className="max-w-[260px]">
                            <DriverContact name={row.driver_name} phone={row.driver_phone} hasLogin={row.has_login} />
                        </TableCell>
                        <TableCell>
                            <Link
                                to={contractInspectionPath(row.contract_id)}
                                onClick={(e) => e.stopPropagation()}
                                className="font-medium hover:underline"
                            >
                                {row.plate ?? 'Unregistered'}
                            </Link>
                            <p className="text-xs text-muted-foreground">{[row.make, row.model].filter(Boolean).join(' ')}</p>
                        </TableCell>
                        <TableCell>
                            <BoardChipBadge chip={row.chip} late={row.late} />
                        </TableCell>
                        <TableCell>
                            <Inspected row={row} />
                        </TableCell>
                        <TableCell>
                            <OpenCounts fixNow={row.open_fix_now} fixSoon={row.open_fix_soon} />
                        </TableCell>
                        <TableCell>
                            <Odometer row={row} />
                        </TableCell>
                        <TableCell>
                            {actionable && <RowActions row={row} drafts={drafts} />}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function BoardCards({ rows, drafts, muted, actionable }: { rows: BoardRow[]; drafts: DraftMarks; muted?: boolean; actionable: boolean }) {
    const navigate = useNavigate();
    return (
        <ul className={cn('space-y-3', muted && 'opacity-75')}>
            {rows.map((row) => (
                <li
                    key={row.contract_id}
                    className="rounded-xl border bg-card p-4 active:bg-accent/40"
                    onClick={() => navigate(contractInspectionPath(row.contract_id))}
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <Link
                                to={contractInspectionPath(row.contract_id)}
                                onClick={(e) => e.stopPropagation()}
                                className="text-lg font-semibold leading-tight"
                            >
                                {row.plate ?? 'Unregistered'}
                            </Link>
                            <p className="text-sm text-muted-foreground">{[row.make, row.model].filter(Boolean).join(' ')}</p>
                        </div>
                        <BoardChipBadge chip={row.chip} late={row.late} className="shrink-0" />
                    </div>
                    <DriverContact name={row.driver_name} phone={row.driver_phone} hasLogin={row.has_login} large className="mt-3" />
                    {(row.open_fix_now > 0 || row.open_fix_soon > 0 || row.inspected_on) && (
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                            <OpenCounts fixNow={row.open_fix_now} fixSoon={row.open_fix_soon} />
                            {row.inspected_on && <Inspected row={row} />}
                        </div>
                    )}
                    {actionable && (
                        <div className="mt-3 empty:hidden" onClick={(e) => e.stopPropagation()}>
                            <RowActions row={row} drafts={drafts} large />
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
}

function HeldWithoutContractGroup({ cars, drafts, actionable }: { cars: HeldWithoutContract[]; drafts: DraftMarks; actionable: boolean }) {
    const navigate = useNavigate();
    if (cars.length === 0) return null;
    return (
        <section className="space-y-3 rounded-xl border border-red-200 bg-red-50/40 p-4 dark:border-red-900 dark:bg-red-950/20">
            <div>
                <h3 className="font-semibold">Held cars with no active contract ({cars.length})</h3>
                <p className="text-sm text-muted-foreground">
                    Repaired before handover. A re-check here is recorded between contracts, and no driver sees it.
                </p>
            </div>
            <ul className="divide-y rounded-lg border bg-background">
                {cars.map((car) => (
                    <li key={car.vehicle_id} className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                            <p className="font-medium">{car.plate ?? 'Unregistered'}</p>
                            <p className="text-sm text-muted-foreground">
                                {[car.make, car.model].filter(Boolean).join(' ')}
                                {car.earliest_raised_on && ` · open since ${formatShortDay(car.earliest_raised_on)}`}
                            </p>
                            <OpenCounts fixNow={car.open_fix_now} fixSoon={car.open_fix_soon} className="mt-1" />
                        </div>
                        {actionable && (
                            <Button className="w-full sm:w-auto" onClick={() => navigate(capturePath(car.vehicle_id, 'recheck'))}>
                                <RotateCcw aria-hidden />
                                {drafts.recheck.has(car.vehicle_id) ? 'Resume re-check' : 'Re-check'}
                            </Button>
                        )}
                    </li>
                ))}
            </ul>
        </section>
    );
}

interface Props {
    /** The Board | Records toggle, rendered at the right of the controls row. */
    controlsExtra?: ReactNode;
}

export default function InspectionBoard({ controlsExtra }: Props) {
    const [requested, setRequested] = useState<string | null>(null);
    const [currentPeriod, setCurrentPeriod] = useState<string | null | undefined>(undefined);
    const [filter, setFilter] = useState<TileKey | null>(null);
    const [search, setSearch] = useState('');
    const { data, isLoading, error, isFetching } = useInspectionBoard(requested);

    useEffect(() => {
        if (requested === null && data) setCurrentPeriod(data.period);
    }, [requested, data]);

    // Drafts live in this browser only; re-read whenever a fresh board arrives.
    const drafts = useMemo<DraftMarks>(() => {
        const marks: DraftMarks = { monthly: new Set(), recheck: new Set() };
        if (!data) return marks;
        for (const d of listDrafts(browserStorage())) {
            if (isResumable(d, data.today)) marks[d.kind].add(d.vehicleId);
        }
        return marks;
    }, [data]);

    const todayMonth = data ? `${data.today.slice(0, 7)}-01` : null;
    const home = currentPeriod ?? todayMonth;
    /*
     * Past rounds, and — before the first round — the months ahead up to the first round, whose
     * board is the first to come back with a period. A year at most, as far as
     * get_contract_inspections looks for the next window.
     */
    const beforeFirstRound = currentPeriod === null;
    const furthest = home && (currentPeriod ? currentPeriod : shiftPeriod(home, 12));
    const onScreen = requested ?? home;
    const reachedFirstRound = beforeFirstRound && requested !== null && !!data?.period;
    const canGoBack = !!data && (data.period !== null || (requested !== null && !!home && requested > home));
    const canGoForward = !!onScreen && !!furthest && onScreen < furthest && !reachedFirstRound;
    // Capture actions belong to the round whose span holds today (see BoardTable).
    const actionable = requested === null;
    const step = (months: number) => {
        if (!onScreen || !home) return;
        const next = shiftPeriod(onScreen, months);
        setRequested(next === home ? null : next);
        setFilter(null);
    };

    const q = search.trim().toLowerCase();
    const qPlate = normalisePlate(search);
    const matches = (plate: string | null, name: string | null) =>
        !q || normalisePlate(plate).includes(qPlate) || (name ?? '').toLowerCase().includes(q);

    const rows = data?.rows.filter((r) => matches(r.plate, r.driver_name)) ?? [];
    const owed = rows.filter((r) => r.chip !== 'not_owed' && (!filter || r.chip === filter));
    const notOwed = filter ? [] : rows.filter((r) => r.chip === 'not_owed');
    const phoneRows = filter === 'done' || q ? owed : owed.filter((r) => r.chip !== 'done');
    const hiddenDone = owed.length - phoneRows.length;
    const heldCars = filter && filter !== 'held' ? [] : (data?.held_without_contract ?? []).filter((c) => matches(c.plate, null));

    const pastRound = requested !== null && !!data?.period && !!currentPeriod && requested < currentPeriod;
    const roundAhead = requested !== null && !!data?.period && !pastRound;
    const requestedMonth = requested ? parseBusinessDay(requested) : null;

    return (
        <Card className="border-0 shadow-none">
            <CardContent className="space-y-5 p-0">
                {/* Round header — dates are the server's; the client builds no month bounds. */}
                <div className="space-y-1">
                    {isLoading ? (
                        <Skeleton className="h-7 w-72" />
                    ) : data?.period ? (
                        <>
                            <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
                                {roundName(data.period)} <span className="text-muted-foreground">· window {windowLabel(data.opens_on, data.closes_on)}</span>
                            </h2>
                            {pastRound && (
                                <p className="text-sm text-muted-foreground">
                                    A past round: who presented is as of that round; held and open items are the car’s today. A backdated
                                    contract status can move a contract into or out of a past round. Inspect and Re-check are on this
                                    round’s board.
                                </p>
                            )}
                            {roundAhead && (
                                <p className="text-sm text-muted-foreground">
                                    Its window has not opened yet. The list is as of today; Inspect and Re-check are on the current board.
                                </p>
                            )}
                        </>
                    ) : requested ? (
                        <h2 className="text-xl font-semibold tracking-tight">No inspection round in {requestedMonth ? format(requestedMonth, 'MMMM yyyy') : 'that month'}</h2>
                    ) : (
                        <>
                            <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">No round yet</h2>
                            <p className="text-sm text-muted-foreground">
                                Monthly inspections start with the first round. Until then the board lists every contract in progress with its
                                car’s condition.
                            </p>
                        </>
                    )}
                </div>

                {/* List controls: search, the round switcher, and the Board | Records toggle. */}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                        <Input
                            placeholder="Search plate or driver"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                            aria-label="Search plate or driver"
                        />
                    </div>
                    <div className="flex items-center gap-1 rounded-md border p-0.5">
                        <Button variant="ghost" size="icon" className="h-11 w-11 sm:h-8 sm:w-8" onClick={() => step(-1)} disabled={!canGoBack || isFetching} aria-label="Previous round">
                            <ChevronLeft />
                        </Button>
                        <span className="min-w-[7.5rem] px-1 text-center text-sm tabular-nums">
                            {onScreen ? format(parseBusinessDay(onScreen)!, 'MMM yyyy') : '…'}
                        </span>
                        <Button variant="ghost" size="icon" className="h-11 w-11 sm:h-8 sm:w-8" onClick={() => step(1)} disabled={!canGoForward || isFetching} aria-label="Next round">
                            <ChevronRight />
                        </Button>
                    </div>
                    {requested !== null && (
                        <Button variant="link" size="sm" className="px-1" onClick={() => setRequested(null)}>
                            This round
                        </Button>
                    )}
                    {controlsExtra && <div className="ml-auto">{controlsExtra}</div>}
                </div>

                {error ? (
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>The board could not be loaded: {(error as Error).message}</AlertDescription>
                    </Alert>
                ) : isLoading || !data ? (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {TILES.map((t) => <Skeleton key={t.key} className="h-20" />)}
                        </div>
                        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                    </div>
                ) : (
                    <>
                        {/* Four tiles, each a filter; tap again to clear. */}
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {TILES.map((t) => {
                                const on = filter === t.key;
                                return (
                                    <button
                                        key={t.key}
                                        type="button"
                                        aria-pressed={on}
                                        onClick={() => setFilter(on ? null : t.key)}
                                        className={cn(
                                            'rounded-xl border-2 p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-4',
                                            on ? t.active : 'border-border hover:bg-accent/50',
                                        )}
                                    >
                                        <span className={cn('block text-2xl font-semibold tabular-nums sm:text-3xl', t.idle)}>{data.counts[t.key]}</span>
                                        <span className="block text-sm font-medium">{t.label}</span>
                                        <span className="hidden text-xs text-muted-foreground sm:block">{t.hint}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {owed.length === 0 && notOwed.length === 0 && heldCars.length === 0 ? (
                            <p className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">
                                {q ? 'No car matches that search.' : filter ? 'No contract in this group.' : 'No contract is in this round.'}
                            </p>
                        ) : (
                            <>
                                {owed.length > 0 && (
                                    <>
                                        <div className="hidden md:block">
                                            <BoardTable rows={owed} drafts={drafts} actionable={actionable} />
                                        </div>
                                        <div className="md:hidden">
                                            <BoardCards rows={phoneRows} drafts={drafts} actionable={actionable} />
                                            {hiddenDone > 0 && (
                                                <button
                                                    type="button"
                                                    className="mt-3 w-full rounded-lg border border-dashed py-3 text-sm text-muted-foreground"
                                                    onClick={() => setFilter('done')}
                                                >
                                                    {hiddenDone} done — show them
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}

                                {notOwed.length > 0 && (
                                    <section className="space-y-2">
                                        <h3 className="text-sm font-medium text-muted-foreground">
                                            Not owed this round · first inspection next round ({notOwed.length})
                                        </h3>
                                        <div className="hidden md:block">
                                            <BoardTable rows={notOwed} drafts={drafts} muted actionable={actionable} />
                                        </div>
                                        <div className="md:hidden">
                                            <BoardCards rows={notOwed} drafts={drafts} muted actionable={actionable} />
                                        </div>
                                    </section>
                                )}

                                <HeldWithoutContractGroup cars={heldCars} drafts={drafts} actionable={actionable} />
                            </>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
